import { Component } from '@theme/component';

/** @extends Component<{video: HTMLVideoElement, control: HTMLButtonElement}> */
class AromachewHeroVideo extends Component {
  requiredRefs = ['video', 'control'];
  #abort;
  #observer;
  #mobile;
  #motion;
  #visible = false;
  #requestedPlayback = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    const { video, control } = this.refs;
    this.#mobile = matchMedia('(max-width: 749px)');
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#visible = false;
    this.#requestedPlayback = !this.#motion.matches && !navigator.connection?.saveData;
    video.muted = true;
    control.hidden = false;
    video.addEventListener('loadeddata', () => { this.dataset.ready = 'true'; }, options);
    video.addEventListener('playing', () => this.#updateControl(), options);
    video.addEventListener('pause', () => this.#updateControl(), options);
    video.addEventListener('error', () => {
      this.dataset.ready = 'false';
      control.hidden = true;
    }, options);
    this.#mobile.addEventListener('change', () => {
      video.pause();
      video.removeAttribute('src');
      video.load();
      this.dataset.ready = 'false';
      this.#syncPlayback();
    }, options);
    this.#motion.addEventListener('change', () => {
      this.#requestedPlayback = !this.#motion.matches && !navigator.connection?.saveData;
      this.#syncPlayback();
    }, options);
    document.addEventListener('visibilitychange', () => this.#syncPlayback(), options);
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#syncPlayback();
    });
    this.#observer.observe(this);
    this.#updateControl();
  }

  #cleanup() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.refs.video?.pause();
  }

  #syncPlayback() {
    const { video } = this.refs;
    if (!this.#requestedPlayback || !this.#visible || document.hidden) {
      video.pause();
      return;
    }
    const source = this.#mobile.matches ? this.dataset.mobileSource : this.dataset.desktopSource;
    if (!source) return;
    if (video.getAttribute('src') !== source) {
      this.dataset.ready = 'false';
      video.src = source;
      video.load();
    }
    video.play().catch((error) => {
      if (error.name === 'NotAllowedError') this.#requestedPlayback = false;
      this.#updateControl();
    });
  }

  #updateControl() {
    const { video, control } = this.refs;
    this.dataset.playing = String(!video.paused);
    control.setAttribute('aria-label', video.paused ? control.dataset.playLabel : control.dataset.pauseLabel);
  }

  togglePlayback() {
    this.#requestedPlayback = this.refs.video.paused;
    this.#syncPlayback();
  }
}

if (!customElements.get('aromachew-hero-video')) {
  customElements.define('aromachew-hero-video', AromachewHeroVideo);
}
